import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { MapEntityDrawerContext } from 'components/Map/MapEntityDrawer';
import { MapEntityDrawerHeader } from 'components/Map/MapEntityDrawerHeader';
import { useContext } from 'react';
import { getResponsiveFontSize } from 'shared/utils';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { useTranslation } from 'react-i18next';
import { useExternalFeatureQuery } from './useExternalFeatureQuery';

type ExternalFeatureDrawerContentProps = {
  sourceId: string;
  featureId?: string | number;
  displayName: string;
};

export const ExternalFeatureDrawerContent = (
  props: ExternalFeatureDrawerContentProps,
): JSX.Element => {
  const { sourceId, featureId, displayName } = props;
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const { externalFeature, isLoading, isError } = useExternalFeatureQuery({
    sourceId,
    featureId,
  });

  return (
    <Box>
      <DrawerStickyHeader
        scrollContainer={scrollElementRef?.current as HTMLElement}
      >
        <DrawerStickyHeader.ContentContainer>
          <MapEntityDrawerHeader>
            <Typography
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography component="span" noWrap>
                {displayName}
              </Typography>
              <Chip
                label={t('map.layers.private')}
                variant="watchduty"
                color="error"
                sx={{ marginLeft: 1 }}
                component="span"
              />
            </Typography>
          </MapEntityDrawerHeader>
        </DrawerStickyHeader.ContentContainer>
      </DrawerStickyHeader>

      {(isLoading || isError || !externalFeature) && (
        <LoadingAndErrors isLoading={isLoading} />
      )}

      {Boolean(externalFeature) && (
        <List>
          {Object.entries(externalFeature!.attributes).map(([key, value]) => (
            <ListItem key={key} divider>
              <ListItemText
                primary={key.replace(/_/g, ' ')}
                secondary={String(value) || '-'}
                slotProps={{
                  primary: {
                    color: 'secondary',
                    variant: 'h4',
                    sx: {
                      fontWeight: 'fontWeightBold',
                      fontSize: getResponsiveFontSize(
                        theme.typography.pxToRem(18),
                      ),
                    },
                  },
                  secondary: {
                    component: 'div',
                    color: 'textPrimary',
                    fontWeight: 'fontWeightMedium',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};
